import { SecondaryButton } from '@src/components/common/dialogStyles';
import LinkIcon from '@src/svgs/LinkIcon';
import VideoIcon from '@src/svgs/VideoIcon';
import styled from 'styled-components';

import type { ReactNode } from 'react';

export type FileCardState = 'resting' | 'progress' | 'failed' | 'done';

export interface FileCardProps {
  /** File name, or the link's host for an import. Truncates; full value on hover. */
  name: string;
  /** One meta line under the name: size, status, or the failure. */
  meta?: ReactNode;
  /** Upload percent, 0–100. Shown as a thin bar and a number while `progress`. */
  percent?: number;
  state?: FileCardState;
  /** What the thumb shows: a video file, or a link being imported. */
  kind?: 'file' | 'link';
  /** Failed state only: a Retry action. */
  onRetry?: () => void;
  /**
   * The thin bar under the text. Off where the card sits in a busy form and
   * the percent alone is enough; a failure still reads from the meta line.
   */
  showBar?: boolean;
  /** Replaces the thumb's icon — e.g. a file's position in an ordered list. */
  thumb?: ReactNode;
  /** Controls after the text, e.g. reorder and remove buttons in a list. */
  actions?: ReactNode;
  className?: string;
}

/**
 * One file the user has handed to Arbor — its name, one line of status and,
 * while it transfers, a thin determinate bar (docs/design-system.md → Drop
 * Zones & File Cards). Used by the Add Stories dialog; built to be reused by
 * any surface that takes a file.
 */
export default function FileCard({
  name,
  meta,
  percent,
  state = 'resting',
  kind = 'file',
  onRetry,
  showBar: barAllowed = true,
  thumb,
  actions,
  className,
}: FileCardProps) {
  const showBar = barAllowed && (state === 'progress' || state === 'failed');
  const clamped = Math.max(0, Math.min(100, Math.round(percent ?? 0)));
  return (
    <Card
      className={className}
      $hasBar={showBar}
      data-testid="file-card"
      data-state={state}
    >
      <Thumb aria-hidden="true">
        {thumb ??
          (kind === 'link' ? (
            <LinkIcon strokeColor="currentColor" width="16" height="16" />
          ) : (
            <VideoIcon strokeColor="currentColor" width="18" height="18" />
          ))}
      </Thumb>
      <Body>
        <Name title={name}>{name}</Name>
        {meta && <Meta $failed={state === 'failed'}>{meta}</Meta>}
      </Body>
      {state === 'progress' && percent !== undefined && (
        <Percent
          // Without the bar, the number is what assistive tech reads.
          {...(showBar
            ? { 'aria-hidden': true }
            : {
                role: 'progressbar',
                'aria-label': `Uploading ${name}`,
                'aria-valuemin': 0,
                'aria-valuemax': 100,
                'aria-valuenow': clamped,
              })}
        >
          {clamped}%
        </Percent>
      )}
      {state === 'failed' && onRetry && (
        <RetryButton type="button" onClick={onRetry}>
          Retry
        </RetryButton>
      )}
      {actions && <Actions>{actions}</Actions>}
      {showBar && (
        <Track
          role="progressbar"
          aria-label={`Uploading ${name}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={clamped}
        >
          <Fill $failed={state === 'failed'} style={{ width: `${clamped}%` }} />
        </Track>
      )}
    </Card>
  );
}

const Card = styled.div<{ $hasBar: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  min-width: 0;
  padding: var(--spacing-sm) var(--spacing-md);
  /* Room under the text for the bar, so it never sits on the meta line. */
  padding-bottom: ${props =>
    props.$hasBar ? 'calc(var(--spacing-sm) + 6px)' : 'var(--spacing-sm)'};
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-button);
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  flex-shrink: 0;
`;

const Thumb = styled.div`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-input);
  background: var(--surface-subtle);
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-variant-numeric: tabular-nums;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Name = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  line-height: var(--leading-text-sm);
  color: var(--foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Meta = styled.span<{ $failed: boolean }>`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
  color: ${props =>
    props.$failed ? 'var(--destructive)' : 'var(--text-muted)'};
`;

const Percent = styled.span`
  flex-shrink: 0;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-secondary);
  font-variant-numeric: tabular-nums;
`;

const RetryButton = styled(SecondaryButton)`
  flex-shrink: 0;
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  font-size: var(--text-xs);
`;

const Track = styled.div`
  position: absolute;
  left: var(--spacing-md);
  right: var(--spacing-md);
  bottom: 6px;
  height: 3px;
  border-radius: 9999px;
  background: color-mix(in srgb, var(--progress-track) 18%, transparent);
  overflow: hidden;
`;

const Fill = styled.div<{ $failed: boolean }>`
  height: 100%;
  border-radius: 9999px;
  background: ${props =>
    props.$failed ? 'var(--destructive)' : 'var(--primary)'};
  transition: width var(--transition-normal);
`;
